import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import ApiService from '../../services/api';
import { Fund } from './types';

/**
 * AI SUMMARY:
 * The FundList component displays a list of funds and allows users to:
 * - View all available funds in a grid layout
 * - Select a fund to view its details
 * - Create a new fund via a button link
 * - Handle loading states and error conditions
 *
 * The component fetches funds from the API on mount and displays them
 * with proper error handling and validation of the API response.
 */
const FundList = () => {
  const [funds, setFunds] = useState<Fund[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFunds = async () => {
      try {
        const token = localStorage.getItem('apiToken');
        if (!token) {
          setError('No API token found');
          setLoading(false);
          return;
        }

        const apiService = new ApiService(token);
        const response = await apiService.getFunds();

        // Ensure response is an array before setting state
        if (Array.isArray(response)) {
          setFunds(response);
        } else {
          console.error(
            'Expected array response from getFunds, got:',
            response,
          );
          setFunds([]);
          setError('Invalid response format from server');
        }

        setLoading(false);
      } catch (error) {
        console.error('Error fetching funds:', error);
        setError('Failed to fetch funds');
        setLoading(false);
      }
    };

    fetchFunds();
  }, []);

  const handleFundSelect = (fundId: string) => {
    navigate(`/fund/details/${fundId}`);
  };

  if (loading) {
    return <div className="text-center py-8">Loading funds...</div>;
  }

  if (error) {
    return <div className="text-center py-8 text-red-500">{error}</div>;
  }

  return (
    <div className="p-6">
      <h1 className="text-2xl font-bold mb-6">Fund Management</h1>

      {funds.length === 0 ? (
        <div className="bg-white p-6 rounded shadow">
          <p className="mb-4">No funds available.</p>
          <Link
            to="/fund/create"
            className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
          >
            Create New Fund
          </Link>
        </div>
      ) : (
        <div className="bg-white p-6 rounded shadow">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-xl font-semibold">Select a Fund</h2>
            <Link
              to="/fund/create"
              className="bg-blue-500 text-white px-4 py-2 rounded hover:bg-blue-600"
            >
              Create New Fund
            </Link>
          </div>
          <div className="mb-6">
            <select
              className="w-full p-2 border rounded focus:outline-none focus:ring-2 focus:ring-blue-500"
              onChange={(e) => handleFundSelect(e.target.value)}
              defaultValue=""
            >
              <option value="" disabled>
                Select a fund
              </option>
              {funds.map((fund) => (
                <option key={fund.id} value={fund.id}>
                  {fund.name}
                </option>
              ))}
            </select>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {funds.map((fund) => (
              <div
                key={fund.id}
                className="border p-4 rounded cursor-pointer hover:bg-gray-50"
                onClick={() => handleFundSelect(fund.id)}
              >
                <h3 className="font-semibold">{fund.name}</h3>
                {fund.description && (
                  <p className="text-gray-600">{fund.description}</p>
                )}
                <p className="text-sm text-gray-500 mt-2">
                  {fund.managers?.length || 0} manager(s) ·{' '}
                  {fund.fundSources?.length || 0} source(s)
                </p>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default FundList;
